import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { ArrowLeft, Save, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';

export default function RouteFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [items, setItems] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshingItems, setRefreshingItems] = useState(false);

  const [form, setForm] = useState({
    route_name: '', item_id: '', revision: '0', description: '',
    bill_of_materials: [],
    output_items: [],  // NEW: Output items (FG + Scrap)
    operations: [],
    is_default: false,
    sheet_metal_bom: null,  // { rm_item_id, blanks_per_strip, strip_width, strip_length_side }
    material_source: 'sheet',  // 'sheet' = standard sheet + cutting plan | 'scrap' = from offcuts, no CTS
  });
  // Fixed assets are never BOM/production material — opt in explicitly when needed.
  const [includeFixedAssets, setIncludeFixedAssets] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [itemsRes, wcRes, gdRes] = await Promise.all([
        inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' }),
        inventoryAPI.listWorkCenters(),
        inventoryAPI.listGodowns(),
      ]);
      setItems(itemsRes.data.items);
      setWorkCenters(wcRes.data);
      setGodowns(gdRes.data || []);
      if (isEdit) {
        const routeRes = await inventoryAPI.getRoute(id);
        const r = routeRes.data;
        setForm({
          route_name: r.route_name, item_id: r.item_id,
          revision: r.revision || '0', description: r.description || '',
          bill_of_materials: r.bill_of_materials || [],
          output_items: r.output_items || [],
          operations: r.operations || [],
          is_default: !!r.is_default,
          sheet_metal_bom: r.sheet_metal_bom || null,
          material_source: r.material_source || 'sheet',
        });
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  // Reload just the item list (keeps form state) so newly-created sheet-metal
  // parts / RM sheets appear immediately and the cutting plan card refreshes.
  const refreshItems = async () => {
    setRefreshingItems(true);
    try {
      const res = await inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' });
      setItems(res.data.items);
      toast.success('Item list refreshed');
    } catch {
      toast.error('Failed to refresh items');
    } finally {
      setRefreshingItems(false);
    }
  };

  const addBOMItem = () => {    setForm(f => ({ ...f, bill_of_materials: [...f.bill_of_materials, { item_id: '', item_name: '', quantity: 1, unit_of_measure: 'Nos' }] }));
  };

  const removeBOMItem = (idx) => {
    setForm(f => ({ ...f, bill_of_materials: f.bill_of_materials.filter((_, i) => i !== idx) }));
  };

  const updateBOMItem = (idx, field, value) => {
    setForm(f => {
      const bom = [...f.bill_of_materials];
      bom[idx] = { ...bom[idx], [field]: value };
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        bom[idx].item_name = item?.item_name || '';
        bom[idx].unit_of_measure = item?.unit_of_measure || 'Nos';
        // Pre-fill the consumption godown from the item's default (scrap -> Scrap Yard etc.)
        const def = godowns.find(g => g.id === item?.default_godown_id)
          || godowns.find(g => g.code === (item?.item_type === 'Scrap' ? 'SCRAP' : 'STORE'));
        bom[idx].source_godown_id = def?.id || '';
        bom[idx].source_godown_name = def?.name || '';
      }
      if (field === 'source_godown_id') {
        bom[idx].source_godown_name = godowns.find(g => g.id === value)?.name || '';
      }
      return { ...f, bill_of_materials: bom };
    });
  };

  // Output Items (FG + Scrap)
  const addOutputItem = (outputType) => {
    setForm(f => ({ ...f, output_items: [...f.output_items, { item_id: '', item_name: '', quantity: 1, unit_of_measure: 'Nos', output_type: outputType }] }));
  };

  const removeOutputItem = (idx) => {
    setForm(f => ({ ...f, output_items: f.output_items.filter((_, i) => i !== idx) }));
  };

  const updateOutputItem = (idx, field, value) => {
    setForm(f => {
      const outputs = [...f.output_items];
      outputs[idx] = { ...outputs[idx], [field]: value };
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        outputs[idx].item_name = item?.item_name || '';
        outputs[idx].unit_of_measure = item?.unit_of_measure || 'Nos';
      }
      return { ...f, output_items: outputs };
    });
  };

  const addOperation = () => {
    const nextNum = (form.operations.length + 1) * 10;
    setForm(f => ({
      ...f, operations: [...f.operations, {
        operation_number: nextNum, operation_description: '', work_center_id: '', work_center_name: '',
        setup_time_minutes: 0, cycle_time_minutes: 0, is_subcontract: false,
        subcontract_supplier_id: '', subcontract_supplier_name: '',
        is_quality_checkpoint: false, quality_parameters: '', work_instructions: '',
      }]
    }));
  };

  const removeOperation = (idx) => {
    setForm(f => ({ ...f, operations: f.operations.filter((_, i) => i !== idx) }));
  };

  const updateOperation = (idx, field, value) => {
    setForm(f => {
      const ops = [...f.operations];
      ops[idx] = { ...ops[idx], [field]: value };
      if (field === 'work_center_id') {
        const wc = workCenters.find(w => w.id === value);
        ops[idx].work_center_name = wc?.name || '';
      }
      return { ...f, operations: ops };
    });
  };

  const handleDefaultToggle = async (checked) => {
    if (!checked) { setForm(f => ({ ...f, is_default: false })); return; }
    if (!form.item_id) { toast.error('Select the item first'); return; }
    try {
      const res = await inventoryAPI.listRoutes({ item_id: form.item_id, include_all_statuses: true });
      const routes = res.data.routes || res.data || [];
      const other = routes.find(r => r.is_default && r.id !== id);
      if (other) {
        toast.warning(`'${other.route_name}' is already the default BOM for this part. Remove its default flag first, then set this one as default.`, { duration: 8000 });
        return; // keep unchecked
      }
    } catch { /* fall through — backend will still enforce on save */ }
    setForm(f => ({ ...f, is_default: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Derive item_id from first FG output item
    const firstFG = form.output_items.find(o => o.output_type === 'finished_good');
    if (!form.route_name) { toast.error('Route name is required'); return; }
    if (!firstFG?.item_id) { toast.error('At least one Finished Good output item is required'); return; }

    let sheetMetalPayload = null;
    if (isSheetMetalPart && usesCuttingPlan) {
      const sm = form.sheet_metal_bom;
      if (!sm || !sm.rm_item_id) { toast.error('Select the raw-material sheet for this sheet-metal part'); return; }
      if (!(parseFloat(sm.strip_width) > 0)) { toast.error('Strip width must be greater than 0'); return; }
      if (!((parseInt(sm.blanks_per_strip) || 0) >= 1)) { toast.error('Blanks per strip must be at least 1'); return; }
      if (partsPerSheet == null || partsPerSheet < 1) { toast.error('Cutting plan yields 0 parts per sheet — check strip width'); return; }
      sheetMetalPayload = {
        rm_item_id: sm.rm_item_id,
        cts_item_id: sm.cts_item_id || '',
        blanks_per_strip: parseInt(sm.blanks_per_strip) || 1,
        strip_width: parseFloat(sm.strip_width),
        strip_length_side: sm.strip_length_side || 'length',
      };
    }
    const submitForm = { ...form, item_id: firstFG.item_id, sheet_metal_bom: sheetMetalPayload };
    if (isScrapSourced && !form.bill_of_materials.some(b => {
      const it = items.find(i => i.id === b.item_id);
      return it && (it.item_type === 'Scrap' || (it.category || '').toLowerCase() === 'scrap');
    })) {
      toast.error('Add the Scrap item (with qty/weight of offcut per part) to the Bill of Materials');
      return;
    }
    if (isPartSourced && !form.bill_of_materials.some(b => {
      const it = items.find(i => i.id === b.item_id);
      return it && ['Sheet Metal Child Part', 'Semi-Finished Part', 'Finished Good'].includes(it.item_type);
    })) {
      toast.error('Add the source part (child / semi-finished / finished good) being converted to the Bill of Materials');
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        const res = await inventoryAPI.updateRoute(id, submitForm);
        if (res.data?.pending_approval) {
          toast.warning(res.data.message || 'BOM changes sent for admin approval', { duration: 9000 });
        } else {
          toast.success('Route updated');
        }
      } else {
        const res = await inventoryAPI.createRoute(submitForm);
        toast.success(res.data?.status === 'pending_approval'
          ? 'Route created — awaiting admin approval'
          : 'Route created');
      }
      navigate('/production/routes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save route');
    } finally { setLoading(false); }
  };

  // Filter items by type for BOM material selection
  const BOM_ELIGIBLE_TYPES = ['Raw Material', 'Service', 'Consumable', 'Semi-Finished Part', 'Sheet Metal Child Part', 'Finished Good', 'Scrap', 'Job Work Item'];
  const bomEligibleItems = items.filter(i =>
    BOM_ELIGIBLE_TYPES.includes(i.item_type) || (includeFixedAssets && i.item_type === 'Fixed Asset')
  );
  const makeItems = items.filter(i => i.make_or_buy?.toLowerCase() === 'make' || i.item_type === 'Finished Good' || i.item_type === 'Sheet Metal Child Part' || i.item_type === 'Semi-Finished Part');
  const scrapEligibleItems = items.filter(i => i.make_or_buy?.toLowerCase() === 'make' || i.item_type === 'Scrap' || i.item_type === 'Finished Good' || i.category?.toLowerCase() === 'scrap');

  // --- Sheet-metal cutting plan helpers ---
  const firstFGItem = (() => {
    const fg = form.output_items.find(o => o.output_type === 'finished_good');
    return fg?.item_id ? items.find(i => i.id === fg.item_id) : null;
  })();
  const isSheetMetalPart = !!firstFGItem && (
    firstFGItem.item_type === 'Sheet Metal Child Part'
    || (firstFGItem.item_type === 'Finished Good' && firstFGItem.category === 'sheet-metal-part')
  );
  const isScrapSourced = form.material_source === 'scrap';
  const isPartSourced = form.material_source === 'part';
  const usesCuttingPlan = !isScrapSourced && !isPartSourced;
  const sheetMetalRMItems = items.filter(i => i.item_type === 'Raw Material' && i.sub_category === 'Sheet Metal');
  const smRM = form.sheet_metal_bom?.rm_item_id ? items.find(i => i.id === form.sheet_metal_bom.rm_item_id) : null;
  const partsPerSheet = (() => {
    const sm = form.sheet_metal_bom;
    if (!sm || !smRM) return null;
    const other = (sm.strip_length_side === 'length') ? smRM.sheet_width : smRM.sheet_length;
    const sw = parseFloat(sm.strip_width), blanks = parseInt(sm.blanks_per_strip) || 1;
    if (!other || !sw || sw <= 0 || sw > other) return null;
    return Math.floor(other / sw) * blanks;
  })();
  const updateSM = (field, value) => setForm(f => ({
    ...f,
    sheet_metal_bom: { blanks_per_strip: 1, strip_length_side: 'length', ...(f.sheet_metal_bom || {}), [field]: value },
  }));

  return (
    <div className="space-y-4 max-w-4xl" data-testid="route-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="font-heading text-xl font-bold tracking-tight">{isEdit ? 'Edit Route' : 'New Route'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Route Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Route Name *</Label>
              <Input value={form.route_name} onChange={e => setForm(f => ({...f, route_name: e.target.value}))}
                placeholder="e.g. Clamp Machining Route" className="h-8 text-xs" data-testid="route-name" />
            </div>
            <div>
              <Label className="text-xs">Primary Output</Label>
              <p className="text-xs text-muted-foreground mt-1 p-2 bg-muted/50 rounded h-8 flex items-center">
                {(() => {
                  const fg = form.output_items.find(o => o.output_type === 'finished_good');
                  if (fg?.item_id) {
                    const item = items.find(i => i.id === fg.item_id);
                    return item ? `${item.unique_id} - ${item.item_name}` : 'Selected below';
                  }
                  return 'Add a Finished Good in Output Items below';
                })()}
              </p>
            </div>
            <div>
              <Label className="text-xs">Revision</Label>
              <Input value={form.revision} onChange={e => setForm(f => ({...f, revision: e.target.value}))}
                className="h-8 text-xs" data-testid="route-revision" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                className="text-xs min-h-[50px]" data-testid="route-description" />
            </div>
            <div className="md:col-span-2 rounded border border-sky-200 bg-sky-50/60 p-2.5">
              <Label className="text-xs font-medium">Material Source</Label>
              <Select value={form.material_source || 'sheet'}
                onValueChange={v => setForm(f => ({ ...f, material_source: v, sheet_metal_bom: v === 'scrap' ? null : f.sheet_metal_bom }))}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="route-material-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sheet">Standard sheet — use the cutting plan (CTS)</SelectItem>
                  <SelectItem value="scrap">Scrap / offcuts — no cutting plan (CTS not considered)</SelectItem>
                  <SelectItem value="part">Another part — convert an existing part (no cutting plan)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                {isScrapSourced
                  ? 'Alternate BOM for parts made from offcuts. No sheet/strip maths — add the Scrap item below with the qty or weight of offcut consumed per part. Tip: name it like "<PART>-FROM-SCRAP" and keep the sheet BOM as the default.'
                  : isPartSourced
                    ? 'Part-to-part conversion (e.g. CTS sheet → Part 1, then some Part 1 → Part 2). No cutting plan — add the source part below with the qty consumed per part; MRP keeps exploding that part through its own BOM. Tip: name it like "<PART2>-FROM-<PART1>".'
                    : 'Parts are cut from a standard sheet; the cutting plan below drives sheets required in MRP.'}
              </p>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input type="checkbox" id="is-default-bom" checked={!!form.is_default}
                onChange={e => handleDefaultToggle(e.target.checked)}
                className="h-4 w-4 accent-amber-500" data-testid="route-is-default" />
              <Label htmlFor="is-default-bom" className="text-xs cursor-pointer">
                Set as <strong>Default BOM</strong> for this item (used by MRP &amp; bulk planning)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Sheet Metal Cutting Plan (only for sheet-metal parts cut from standard sheets) */}
        {isSheetMetalPart && usesCuttingPlan && (
          <Card className="border-amber-300" data-testid="sheet-metal-bom-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                Sheet Metal Cutting Plan
                <span className="text-[10px] uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded">Yield</span>
              </CardTitle>
              <p className="text-[11px] text-muted-foreground">Defines how this part is cut from a standard sheet. Parts/Sheet = FLOOR(other dimension ÷ strip width) × blanks per strip.</p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Raw Material Sheet *</Label>
                <SearchableSelect
                  options={sheetMetalRMItems.map(i => ({
                    value: i.id,
                    label: `${i.item_name} — ${i.unique_id} (${i.sheet_grade || '?'}, ${i.sheet_thickness || '?'}mm, ${i.sheet_length || '?'}×${i.sheet_width || '?'})`,
                    searchStr: `${i.item_name} ${i.unique_id} ${i.sheet_grade || ''}`,
                  }))}
                  value={form.sheet_metal_bom?.rm_item_id || ''}
                  onSelect={v => updateSM('rm_item_id', v)}
                  placeholder="Select the standard sheet this part is cut from…"
                  emptyText="No sheet-metal raw materials. Create a Raw Material with Sub-Category 'Sheet Metal' first."
                  testId="sm-rm-select"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">CTS Stock Item (from BOM)</Label>
                <SearchableSelect
                  options={form.bill_of_materials
                    .map(b => items.find(i => i.id === b.item_id))
                    .filter(Boolean)
                    .filter(i => i.id !== form.sheet_metal_bom?.rm_item_id)
                    .map(i => ({ value: i.id, label: `${i.item_name} — ${i.unique_id}`, searchStr: `${i.item_name} ${i.unique_id}` }))}
                  value={form.sheet_metal_bom?.cts_item_id || ''}
                  onSelect={v => updateSM('cts_item_id', v)}
                  placeholder="Link the cut-to-size item this cutting plan produces…"
                  emptyText="Add the CTS-size item to the Bill of Materials below first, then link it here."
                  testId="sm-cts-item-select"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Received cut pieces are stocked against this item. Pick the pre-created CTS-size item from the Bill of Materials.</p>
              </div>
              <div>
                <Label className="text-xs">Strip Length = which sheet dimension *</Label>
                <Select value={form.sheet_metal_bom?.strip_length_side || 'length'} onValueChange={v => updateSM('strip_length_side', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="sm-strip-side"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="length">Length side{smRM ? ` (${smRM.sheet_length}mm)` : ''}</SelectItem>
                    <SelectItem value="width">Width side{smRM ? ` (${smRM.sheet_width}mm)` : ''}</SelectItem>
                  </SelectContent>
                </Select>
                {smRM && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Strips cut across {form.sheet_metal_bom?.strip_length_side === 'width' ? `${smRM.sheet_length}mm` : `${smRM.sheet_width}mm`}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Strip Width (mm) *</Label>
                <Input type="number" step="any" className="h-8 text-xs" value={form.sheet_metal_bom?.strip_width || ''}
                  onChange={e => updateSM('strip_width', e.target.value)} placeholder="e.g. 120" data-testid="sm-strip-width" />
              </div>
              <div>
                <Label className="text-xs">Blanks per Strip *</Label>
                <Input type="number" step="1" min="1" className="h-8 text-xs" value={form.sheet_metal_bom?.blanks_per_strip || ''}
                  onChange={e => updateSM('blanks_per_strip', e.target.value)} placeholder="e.g. 4" data-testid="sm-blanks" />
              </div>
              <div className="flex items-end">
                <div className="w-full p-2 rounded bg-amber-50 border border-amber-200">
                  <div className="text-[10px] text-muted-foreground">Parts per Sheet (auto)</div>
                  <div className="text-lg font-bold text-amber-700 font-mono" data-testid="sm-parts-per-sheet">
                    {partsPerSheet != null ? partsPerSheet : '—'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bill of Materials */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Bill of Materials</CardTitle>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={includeFixedAssets}
                    onChange={e => setIncludeFixedAssets(e.target.checked)}
                    className="h-3.5 w-3.5 accent-slate-600" data-testid="bom-include-fixed-assets" />
                  Include fixed assets
                </label>
                <Button type="button" size="sm" variant="outline" onClick={addBOMItem} data-testid="add-bom-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Material
                </Button>
              </div>
            </div>
            {isScrapSourced && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="scrap-bom-hint">
                Scrap-sourced BOM: include the Scrap item and set its quantity to the offcut consumed per part (e.g. 0.8 Kg).
              </p>
            )}
            {isPartSourced && (
              <p className="text-[11px] text-sky-700 mt-1" data-testid="part-bom-hint">
                Part-sourced BOM: include the source part (child / semi-finished / finished good) and set its quantity to the number consumed per part produced.
              </p>
            )}
          </CardHeader>
          <CardContent>
            {form.bill_of_materials.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No materials added</p>
            ) : (
              <div className="space-y-2">
                {form.bill_of_materials.map((bom, idx) => (
                  <div key={idx} className="flex gap-2 items-end p-2 rounded border bg-muted/30" data-testid={`bom-row-${idx}`}>
                    <div className="flex-1">
                      <Label className="text-[10px]">Material</Label>
                      <SearchableSelect
                        options={bomEligibleItems.map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: i.item_type, searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''}` }))}
                        value={bom.item_id}
                        onSelect={v => updateBOMItem(idx, 'item_id', v)}
                        placeholder="Search materials..."
                        emptyText="No items."
                        triggerClassName="h-7"
                      />
                    </div>
                    <div className="w-24">
                      <Label className="text-[10px]">Qty</Label>
                      <Input type="number" step="0.01" value={bom.quantity}
                        onChange={e => updateBOMItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs" />
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px]">UOM</Label>
                      <Input value={bom.unit_of_measure} disabled className="h-7 text-xs bg-muted" />
                    </div>
                    <div className="w-44">
                      <Label className="text-[10px]">Consume from</Label>
                      <Select value={bom.source_godown_id || ''}
                        onValueChange={v => updateBOMItem(idx, 'source_godown_id', v)}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`bom-godown-${idx}`}>
                          <SelectValue placeholder="Item default" />
                        </SelectTrigger>
                        <SelectContent>
                          {godowns.map(g => (
                            <SelectItem key={g.id} value={g.id} className="text-xs">{g.code} - {g.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeBOMItem(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Output Items (FG + Scrap) */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-medium">Output Items</CardTitle>
                <p className="text-[10px] text-muted-foreground mt-0.5">Define what this route produces: Finished Goods + Scrap</p>
              </div>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="ghost" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50" onClick={refreshItems} disabled={refreshingItems} title="Refresh item list & cutting plan" data-testid="refresh-items-btn">
                  <RefreshCw className={`w-3 h-3 mr-1 ${refreshingItems ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => addOutputItem('finished_good')} data-testid="add-fg-btn">
                  <Plus className="w-3 h-3 mr-1" /> Finished Good
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => addOutputItem('scrap')} data-testid="add-scrap-btn">
                  <Plus className="w-3 h-3 mr-1" /> Scrap
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {form.output_items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No output items defined. Add finished goods and/or scrap produced by this route.</p>
            ) : (
              <div className="space-y-2">
                {form.output_items.map((out, idx) => (
                  <div key={idx} className={`flex items-end gap-2 p-2 rounded border ${out.output_type === 'scrap' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`} data-testid={`output-row-${idx}`}>
                    <div className="w-14">
                      <Badge variant={out.output_type === 'scrap' ? 'secondary' : 'default'} className="text-[9px]">
                        {out.output_type === 'scrap' ? 'SCRAP' : 'FG'}
                      </Badge>
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px]">Item</Label>
                      <SearchableSelect
                        options={(out.output_type === 'scrap' ? scrapEligibleItems : makeItems).map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: i.item_type, searchStr: `${i.unique_id} ${i.item_name}` }))}
                        value={out.item_id}
                        onSelect={v => updateOutputItem(idx, 'item_id', v)}
                        placeholder={out.output_type === 'scrap' ? "Search scrap/make items..." : "Search make items..."}
                        emptyText="No items."
                        testId={`output-item-${idx}`}
                      />
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px]">Qty</Label>
                      <Input type="number" step="0.01" value={out.quantity}
                        onChange={e => updateOutputItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs" />
                    </div>
                    <div className="w-20">
                      <Label className="text-[10px]">UOM</Label>
                      <Input value={out.unit_of_measure} disabled className="h-7 text-xs bg-muted" />
                    </div>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeOutputItem(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Operations */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Operations</CardTitle>
              <Button type="button" size="sm" variant="outline" onClick={addOperation} data-testid="add-operation-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Operation
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {form.operations.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No operations added</p>
            ) : (
              <div className="space-y-3">
                {form.operations.map((op, idx) => (
                  <div key={idx} className="p-3 rounded border bg-muted/30 space-y-2" data-testid={`op-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">Op {op.operation_number}</span>
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeOperation(idx)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px]">Op Number</Label>
                        <Input type="number" value={op.operation_number} onChange={e => updateOperation(idx, 'operation_number', parseInt(e.target.value) || 0)}
                          className="h-7 text-xs" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-[10px]">Description</Label>
                        <Input value={op.operation_description} onChange={e => updateOperation(idx, 'operation_description', e.target.value)}
                          placeholder="Operation description" className="h-7 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Work Center</Label>
                        <Select value={op.work_center_id || 'none'} onValueChange={v => updateOperation(idx, 'work_center_id', v === 'none' ? '' : v)}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {workCenters.map(wc => <SelectItem key={wc.id} value={wc.id}>{wc.code} - {wc.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px]">Setup Time (min)</Label>
                        <Input type="number" value={op.setup_time_minutes} onChange={e => updateOperation(idx, 'setup_time_minutes', parseFloat(e.target.value) || 0)}
                          className="h-7 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Cycle Time (min)</Label>
                        <Input type="number" value={op.cycle_time_minutes} onChange={e => updateOperation(idx, 'cycle_time_minutes', parseFloat(e.target.value) || 0)}
                          className="h-7 text-xs" />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <Switch checked={op.is_subcontract} onCheckedChange={v => updateOperation(idx, 'is_subcontract', v)} />
                        <Label className="text-[10px]">Subcontract</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={op.is_quality_checkpoint} onCheckedChange={v => updateOperation(idx, 'is_quality_checkpoint', v)} />
                        <Label className="text-[10px]">Quality Checkpoint</Label>
                      </div>
                    </div>
                    {op.is_quality_checkpoint && (
                      <div>
                        <Label className="text-[10px]">Quality Parameters</Label>
                        <Input value={op.quality_parameters} onChange={e => updateOperation(idx, 'quality_parameters', e.target.value)}
                          placeholder="Parameters to check" className="h-7 text-xs" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-route-btn">
            <Save className="w-4 h-4 mr-1" /> {loading ? 'Saving...' : isEdit ? 'Update Route' : 'Create Route'}
          </Button>
        </div>
      </form>
    </div>
  );
}
